import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader, PageBody } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { CURRENCIES, CURRENCY_SYMBOLS } from "@/lib/currency";
import { toast } from "sonner";
import { Plus, PencilSimple } from "@phosphor-icons/react";

const emptyForm = () => ({
  name: "",
  currency: "USD",
  consolidation_group: "",
  vat_number: "",
  country: "",
  employee_prefix: "",
});
const emptyBranding = () => ({
  legal_name: "",
  address: "",
  tax_id: "",
  phone: "",
  email: "",
  bank_details: "",
  footer_text: "",
});

export default function CompaniesPage() {
  const { companies, loadCompanies, formatApiError } = useAuth();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null | company object
  const [form, setForm] = useState(emptyForm());
  const [branding, setBranding] = useState(emptyBranding());
  const [loadingBranding, setLoadingBranding] = useState(false);
  const [lookingUp, setLookingUp] = useState(null); // "ch" | "vat" | null

  const lookupCompaniesHouse = async () => {
    if (!branding.tax_id) return;
    setLookingUp("ch");
    try {
      const { data } = await api.get(
        `/lookup/companies-house/${encodeURIComponent(branding.tax_id.trim())}`
      );
      if (!data.found) {
        toast.error("Company not found on Companies House");
        return;
      }
      setBranding((b) => ({
        ...b,
        legal_name: data.legal_name || b.legal_name,
        address: data.address || b.address,
      }));
      setForm((f) => ({ ...f, country: f.country || "GB" }));
      toast.success(`Companies House: ${data.legal_name} (${data.status})`);
    } catch (e) {
      const detail = formatApiError(e.response?.data?.detail) || e.message;
      if (e.response?.status === 503) {
        toast.error("Companies House API key not configured. Add COMPANIES_HOUSE_API_KEY to backend .env.");
      } else {
        toast.error(detail);
      }
    } finally {
      setLookingUp(null);
    }
  };

  const lookupVAT = async () => {
    if (!form.vat_number) return;
    setLookingUp("vat");
    try {
      const { data } = await api.post(`/lookup/vat`, {
        vat_number: form.vat_number,
        country_code: form.country || undefined,
      });
      if (!data.valid) {
        toast.error(`VAT number not valid${data.error ? ": " + data.error.slice(0, 80) : ""}`);
        return;
      }
      setBranding((b) => ({
        ...b,
        legal_name: data.legal_name || b.legal_name,
        address: data.address || b.address,
      }));
      if (data.country_code && !form.country) {
        setForm((f) => ({ ...f, country: data.country_code }));
      }
      const src = data.source === "hmrc" ? "HMRC" : "VIES";
      toast.success(`${src} verified: ${data.legal_name || "VAT number valid"}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLookingUp(null);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setBranding(emptyBranding());
    setOpen(true);
  };

  const openEdit = async (c) => {
    setEditing(c);
    setForm({
      name: c.name || "",
      currency: c.currency || "USD",
      consolidation_group: c.consolidation_group || "",
      vat_number: c.vat_number || "",
      country: c.country || "",
      employee_prefix: c.employee_prefix || "",
    });
    setBranding(emptyBranding());
    setOpen(true);
    // Fetch branding to prefill contact/legal fields
    setLoadingBranding(true);
    try {
      const { data } = await api.get(`/companies/${c.id}/branding`);
      setBranding({
        legal_name: data.legal_name || "",
        address: data.address || "",
        tax_id: data.tax_id || "",
        phone: data.phone || "",
        email: data.email || "",
        bank_details: data.bank_details || "",
        footer_text: data.footer_text || "",
      });
    } catch (e) {
      // no branding yet — leave empty
    } finally {
      setLoadingBranding(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      // Auto-derive short Name from Legal Name if user left it blank
      const shortName = form.name?.trim() || branding.legal_name?.trim();
      if (!shortName) {
        toast.error("Please enter a Legal Company Name or Short Name");
        return;
      }
      if (editing) {
        const payload = { ...form, name: shortName };
        Object.keys(payload).forEach((k) => {
          if (payload[k] === "") payload[k] = null;
        });
        await api.patch(`/companies/${editing.id}`, payload);
        await api.patch(`/companies/${editing.id}/branding`, branding);
        toast.success(
          form.vat_number && !editing.vat_number
            ? "Company updated — Making Tax Digital tab is now enabled"
            : "Company details saved"
        );
      } else {
        // Create company first
        const { data: created } = await api.post(`/companies`, {
          name: shortName,
          currency: form.currency,
          consolidation_group: form.consolidation_group || null,
          vat_number: form.vat_number || null,
          country: form.country || null,
          employee_prefix: form.employee_prefix || null,
        });
        // Save branding contact/legal details
        const hasBranding = Object.values(branding).some((v) => v && v.trim());
        if (hasBranding) {
          try {
            await api.patch(`/companies/${created.id}/branding`, branding);
          } catch (e2) {
            // Branding fields are optional; don't fail creation
          }
        }
        toast.success("Company created");
      }
      setOpen(false);
      setEditing(null);
      setForm(emptyForm());
      setBranding(emptyBranding());
      await loadCompanies();
    } catch (e2) {
      if (e2.response?.status === 402) {
        const detail = formatApiError(e2.response?.data?.detail) || "Company limit reached";
        toast.error(detail, {
          action: {
            label: "Upgrade",
            onClick: () => (window.location.href = "/subscription"),
          },
          duration: 10000,
        });
      } else {
        toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
      }
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Administration"
        title="Companies"
        description="Each company keeps a separate ledger. Set a VAT number to enable HMRC Making Tax Digital."
        actions={
          <Button
            onClick={openCreate}
            data-testid="new-company-btn"
            className="rounded-sm h-9 font-mono uppercase tracking-wider text-xs"
          >
            <Plus size={14} className="mr-2" /> New Company
          </Button>
        }
      />

      <Dialog
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) {
            setEditing(null);
            setForm(emptyForm());
            setBranding(emptyBranding());
          }
        }}
      >
        <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">
              {editing ? `Edit — ${editing.name}` : "New Company"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <div className="overline text-muted-foreground mb-2">
                Company Information
              </div>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest">Legal Company Name *</Label>
                  <Input
                    data-testid="company-legal-name"
                    value={branding.legal_name}
                    onChange={(e) => setBranding({ ...branding, legal_name: e.target.value })}
                    placeholder="e.g. Acme Holdings Ltd"
                    className="rounded-sm font-mono"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Address</Label>
                  <textarea
                    data-testid="company-address"
                    value={branding.address}
                    onChange={(e) => setBranding({ ...branding, address: e.target.value })}
                    rows={2}
                    placeholder="Street, city, postcode"
                    className="w-full rounded-sm font-mono border border-input bg-transparent px-3 py-2 text-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest">Company Number</Label>
                    <div className="flex gap-2">
                      <Input
                        data-testid="company-tax-id"
                        value={branding.tax_id}
                        onChange={(e) => setBranding({ ...branding, tax_id: e.target.value })}
                        placeholder="e.g. 12345678"
                        className="rounded-sm font-mono"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={lookupCompaniesHouse}
                        disabled={lookingUp === "ch" || !branding.tax_id}
                        data-testid="lookup-ch-btn"
                        className="rounded-sm font-mono text-xs uppercase tracking-wider whitespace-nowrap"
                        title="Look up via Companies House (UK)"
                      >
                        {lookingUp === "ch" ? "…" : "Lookup"}
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest">Country</Label>
                    <Input
                      data-testid="company-country"
                      value={form.country}
                      onChange={(e) => setForm({ ...form, country: e.target.value })}
                      placeholder="e.g. GB"
                      maxLength={16}
                      className="rounded-sm font-mono"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest">Email Address</Label>
                    <Input
                      data-testid="company-email"
                      type="email"
                      value={branding.email}
                      onChange={(e) => setBranding({ ...branding, email: e.target.value })}
                      placeholder="finance@company.com"
                      className="rounded-sm font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest">Phone Number</Label>
                    <Input
                      data-testid="company-phone"
                      value={branding.phone}
                      onChange={(e) => setBranding({ ...branding, phone: e.target.value })}
                      placeholder="+44 20 1234 5678"
                      className="rounded-sm font-mono"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">
                    VAT Number (enables Making Tax Digital)
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      data-testid="company-vat-number"
                      value={form.vat_number}
                      onChange={(e) => setForm({ ...form, vat_number: e.target.value })}
                      placeholder="e.g. GB123456789 or DE123456789"
                      className="rounded-sm font-mono"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={lookupVAT}
                      disabled={lookingUp === "vat" || !form.vat_number}
                      data-testid="lookup-vat-btn"
                      className="rounded-sm font-mono text-xs uppercase tracking-wider whitespace-nowrap"
                      title="Verify via HMRC (GB) or VIES (EU)"
                    >
                      {lookingUp === "vat" ? "…" : "Verify"}
                    </Button>
                  </div>
                </div>
                {form.vat_number && (
                  <div className="border border-primary/30 bg-primary/5 p-3 text-xs">
                    <strong>Making Tax Digital</strong> will appear in the sidebar for this company. Configure it under the MTD tab.
                  </div>
                )}
                <div>
                  <Label className="text-xs uppercase tracking-widest">
                    Employee Number Prefix
                  </Label>
                  <Input
                    data-testid="company-employee-prefix"
                    value={form.employee_prefix}
                    onChange={(e) => setForm({ ...form, employee_prefix: e.target.value.toUpperCase() })}
                    placeholder="e.g. WRW → WRW-001, WRW-002…"
                    maxLength={8}
                    className="rounded-sm font-mono uppercase"
                  />
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                    Used to auto-generate employee numbers for expense claims. Defaults to "EMP" if blank.
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-border">
              <div className="overline text-muted-foreground mb-2">
                Ledger Settings
              </div>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest">Short Name (sidebar)</Label>
                  <Input
                    data-testid="company-name-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Auto-filled from Legal Name if left blank"
                    className="rounded-sm font-mono"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest">Base Currency</Label>
                    <Select
                      value={form.currency}
                      onValueChange={(v) => setForm({ ...form, currency: v })}
                      disabled={!!editing}
                    >
                      <SelectTrigger data-testid="company-currency" className="rounded-sm font-mono">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CURRENCIES.map((c) => (
                          <SelectItem key={c} value={c} className="font-mono">
                            {CURRENCY_SYMBOLS[c]}  {c}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {editing && (
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                        Locked once journals exist.
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest">Consolidation Group</Label>
                    <Input
                      data-testid="company-group-input"
                      value={form.consolidation_group}
                      onChange={(e) => setForm({ ...form, consolidation_group: e.target.value })}
                      placeholder="optional"
                      className="rounded-sm font-mono"
                    />
                  </div>
                </div>
              </div>
            </div>

            {editing && (
              <div className="pt-4 border-t border-border space-y-3">
                <div className="overline text-muted-foreground">
                  Invoice & Statement Details
                </div>
                {loadingBranding && (
                  <div className="text-xs text-muted-foreground">Loading current details…</div>
                )}
                <div>
                  <Label className="text-xs uppercase tracking-widest">Bank Details (invoice remittance)</Label>
                  <textarea
                    data-testid="company-bank-details"
                    value={branding.bank_details}
                    onChange={(e) => setBranding({ ...branding, bank_details: e.target.value })}
                    rows={2}
                    placeholder="Sort code, account, IBAN/SWIFT…"
                    className="w-full rounded-sm font-mono border border-input bg-transparent px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Invoice Footer Text</Label>
                  <Input
                    data-testid="company-footer-text"
                    value={branding.footer_text}
                    onChange={(e) => setBranding({ ...branding, footer_text: e.target.value })}
                    placeholder="Thank you for your business."
                    className="rounded-sm font-mono"
                  />
                </div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  Logo & invoice template live under Settings → Branding.
                </div>
              </div>
            )}
            <DialogFooter>
              <Button
                type="submit"
                data-testid="company-submit"
                className="rounded-sm font-mono uppercase tracking-wider text-xs"
              >
                {editing ? "Save Changes" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <PageBody>
        <div className="border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/40">
              <tr className="text-left">
                <Th>Legal Name</Th>
                <Th>Company #</Th>
                <Th>VAT #</Th>
                <Th>Country</Th>
                <Th>Email</Th>
                <Th>Phone</Th>
                <Th>Currency</Th>
                <Th>Group</Th>
                <Th>Role</Th>
                <Th className="text-right pr-4">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {companies.map((c) => (
                <tr key={c.id} data-testid={`company-row-${c.id}`} className="border-b border-border last:border-b-0 hover:bg-muted/40">
                  <Td className="font-mono">
                    <div>{c.legal_name || c.name}</div>
                    {c.legal_name && c.legal_name !== c.name && (
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{c.name}</div>
                    )}
                  </Td>
                  <Td className="font-mono text-muted-foreground">{c.tax_id || "—"}</Td>
                  <Td className="font-mono text-muted-foreground">{c.vat_number || "—"}</Td>
                  <Td className="font-mono text-muted-foreground">{c.country || "—"}</Td>
                  <Td className="font-mono text-muted-foreground text-xs">{c.email || "—"}</Td>
                  <Td className="font-mono text-muted-foreground text-xs">{c.phone || "—"}</Td>
                  <Td className="num">{c.currency}</Td>
                  <Td className="text-muted-foreground">
                    {c.consolidation_group || "—"}
                  </Td>
                  <Td>
                    <span className="tag-badge capitalize">{c.role}</span>
                  </Td>
                  <Td className="text-right pr-4">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openEdit(c)}
                      data-testid={`edit-company-${c.id}`}
                      className="rounded-sm h-8 font-mono text-xs uppercase tracking-wider"
                      disabled={c.role !== "admin"}
                      title={c.role !== "admin" ? "Admin only" : "Edit"}
                    >
                      <PencilSimple size={12} className="mr-1" /> Edit
                    </Button>
                  </Td>
                </tr>
              ))}
              {companies.length === 0 && (
                <tr>
                  <td colSpan={10} className="text-center py-8 text-muted-foreground">
                    No companies yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </PageBody>
    </>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 overline ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 ${className}`}>{children}</td>
);
